import cn from "classnames";
import { memo, useMemo } from "react";
import {
  Route,
  Navigate,
  useRoutes,
  createRoutesFromElements,
} from "react-router-dom";
import { Overlay } from "@rshbintech.dboul/core-ui";
import { getMenuItemsSettings } from "../utils";
import { useProtectedRoutes } from "../hooks";
import {
  AccountOpeningApp,
  AccreditivsApp,
  AcquiringApp,
  AnalyticsApp,
  AusnApp,
  BrokerageServicesApp,
  CalendarApp,
  CardsApp,
  CashManagementApp,
  CashWithdrawalApp,
  ContactsApp,
  CounterpartiesApp,
  CurrencyExchangeApp,
  CurrencyTradingApp,
  DealsApp,
  DepositsApp,
  ForeignActivityApp,
  GoodsAndServicesApp,
  HelpApp,
  InvoicesApp,
  LoansApp,
  LoansTransactionApp,
  LoyaltyProgramApp,
  MainApp,
  MessagesApp,
  MyBusinessApp,
  NotificationsApp,
  NsdCertificateApp,
  PaymentsApp,
  ProfileApp,
  PromissoryNotesApp,
  RckApp,
  ReferencesApp,
  RegistriesApp,
  SalaryApp,
  SbpApp,
  ShowcaseApp,
  StatementsApp,
  TariffsApp,
  TransfersApp,
  WebsitesConstructorApp,
  WriteOffRegistersApp,
  SwiftMessagesApp,
  TrusteeReportsApp,
} from "./app-loader";
import { ContentProps } from "./types";
import styles from "./styles.scss";

export const Content = memo(
  ({
    isMenuOpen,
    mainPagePath,
    onOverlayClick,
    ...contentProps
  }: ContentProps) => {
    const { validateRouteForBankForBanksUser, validateRoute } =
      useProtectedRoutes();

    const handleOverlayClick = () => {
      if (isMenuOpen) {
        onOverlayClick?.();
      }
    };

    const { clientSettings } = contentProps;

    const defaultRoutesConfig = createRoutesFromElements(
      <>
        <Route
          path="account-opening/*"
          element={<AccountOpeningApp {...contentProps} />}
        />
        <Route
          path="accreditivs/*"
          element={<AccreditivsApp {...contentProps} />}
        />
        <Route
          path="write-off-registers/*"
          element={<WriteOffRegistersApp {...contentProps} />}
        />
        <Route
          path="acquiring/*"
          element={<AcquiringApp {...contentProps} />}
        />
        <Route
          path="analytics/*"
          element={<AnalyticsApp {...contentProps} />}
        />
        <Route path="ausn/*" element={<AusnApp {...contentProps} />} />
        <Route
          path="brokerage-services/*"
          element={<BrokerageServicesApp {...contentProps} />}
        />
        <Route path="cards/*" element={<CardsApp {...contentProps} />} />
        <Route
          path="cash-management/*"
          element={<CashManagementApp {...contentProps} />}
        />
        <Route
          path="cash-withdrawal/*"
          element={<CashWithdrawalApp {...contentProps} />}
        />
        <Route
          path="counterparties/*"
          element={<CounterpartiesApp {...contentProps} />}
        />
        <Route
          path="currency-trading/*"
          element={<CurrencyTradingApp {...contentProps} />}
        />
        <Route path="deposits/*" element={<DepositsApp {...contentProps} />} />
        <Route
          path="promissory-notes/*"
          element={<PromissoryNotesApp {...contentProps} />}
        />
        <Route
          path="foreign-activity/*"
          element={<ForeignActivityApp {...contentProps} />}
        />
        <Route
          path="foreign-activity/currency-exchange/*"
          element={<CurrencyExchangeApp {...contentProps} />}
        />
        <Route
          path="crm/goods-and-services/*"
          element={<GoodsAndServicesApp {...contentProps} />}
        />
        <Route
          path="crm/websites-constructor/*"
          element={<WebsitesConstructorApp {...contentProps} />}
        />
        <Route
          path="crm/calendar/*"
          element={<CalendarApp {...contentProps} />}
        />
        <Route
          path="crm/contacts/*"
          element={<ContactsApp {...contentProps} />}
        />
        <Route path="crm/deals/*" element={<DealsApp {...contentProps} />} />
        <Route path="help/*" element={<HelpApp {...contentProps} />} />
        <Route path="invoices/*" element={<InvoicesApp {...contentProps} />} />
        <Route path="loans/*" element={<LoansApp {...contentProps} />} />
        <Route
          path="loans-transaction/*"
          element={<LoansTransactionApp {...contentProps} />}
        />
        <Route
          path="loyalty-program/*"
          element={<LoyaltyProgramApp {...contentProps} />}
        />
        <Route path="main/*" element={<MainApp {...contentProps} />} />
        <Route path="messages/*" element={<MessagesApp {...contentProps} />} />
        <Route
          path="my-business/*"
          element={<MyBusinessApp {...contentProps} />}
        />
        <Route
          path="notifications/*"
          element={<NotificationsApp {...contentProps} />}
        />
        <Route
          path="nsd-certificate/*"
          element={<NsdCertificateApp {...contentProps} />}
        />
        <Route path="payments/*" element={<PaymentsApp {...contentProps} />} />
        <Route path="profile/*" element={<ProfileApp {...contentProps} />} />
        <Route path="rck/*" element={<RckApp {...contentProps} />} />
        <Route
          path="references/*"
          element={<ReferencesApp {...contentProps} />}
        />
        <Route
          path="registries/*"
          element={<RegistriesApp {...contentProps} />}
        />
        <Route path="salary/*" element={<SalaryApp {...contentProps} />} />
        <Route path="sbp/*" element={<SbpApp {...contentProps} />} />
        <Route path="showcase/*" element={<ShowcaseApp {...contentProps} />} />
        <Route
          path="statements/*"
          element={<StatementsApp {...contentProps} />}
        />
        <Route path="tariffs/*" element={<TariffsApp {...contentProps} />} />
        <Route
          path="transfers/*"
          element={<TransfersApp {...contentProps} />}
        />
        <Route
          path="swift-messages/*"
          element={<SwiftMessagesApp {...contentProps} />}
        />
        <Route
          path="trustee-reports/*"
          element={<TrusteeReportsApp {...contentProps} />}
        />
      </>
    );

    /**
     * INFO: Управление отображением пунктов меню происходит:
     *
     * 1. Через АРМ Администратора и МС Настройки.
     * 2. Согласно полномочиям которыми обладает пользователь.
     *
     * Кроме отображения/скрытия пунктов меню слева, нам так же необходимо ограничить вход
     * в проект через адресную строку браузера, которое недоступно в меню.
     * */
    const filteredRoutesConfig = useMemo(
      () =>
        defaultRoutesConfig.map((route) => {
          const MainPageNavigationElement = (
            <Navigate to={mainPagePath ?? ""} />
          );

          /**
           * INFO: Берём часть пути до замыкающей звёздочки
           * Например: foreign-activity/currency-exchange/* -> foreign-activity/currency-exchange, main/* -> main
           */
          const leadingPath = route.path?.split(/\/\*/)[0];

          if (!leadingPath) {
            return route;
          }

          // Проверяем доступность роута для юзера который относится к категории "банк для банков"
          let routeAvailable = validateRouteForBankForBanksUser(
            leadingPath,
            false
          );

          // Проверяем что у пользователя достаточно полномочий для просмотра роута
          routeAvailable &&= validateRoute(leadingPath);

          // INFO: Если роут не доступен, то не позволяем пользователю войти в проект и делаем редирект на Главную.
          if (!routeAvailable) {
            route.element = MainPageNavigationElement;

            return route;
          }

          // INFO: Фильтруем конфигурацию роутов по умолчанию, основываясь на конкретной настройке пункта меню
          // INFO: Проверяем наличие настроек отображения пунктов меню из МС Настройки
          if (clientSettings?.settings?.frontend) {
            // INFO: Получаем список настроек меню
            const menuItemsSettings = getMenuItemsSettings(clientSettings);

            // INFO: Находим соответствующую конкретному path настройку
            const menuItemSetting = menuItemsSettings.find(
              (menuItemSetting) => menuItemSetting.key === "/" + leadingPath
            );

            if (!menuItemSetting) {
              return route;
            }

            // INFO: Если настройка пункта меню имеет значение false,
            // то не позволяем пользователю войти в проект и делаем редирект на Главную.
            if (menuItemSetting.value === "false") {
              route.element = MainPageNavigationElement;
            }
          }

          return route;
        }),
      [
        mainPagePath,
        validateRoute,
        clientSettings,
        defaultRoutesConfig,
        validateRouteForBankForBanksUser,
      ]
    );

    const routes = useRoutes(filteredRoutesConfig);

    return (
      <>
        <main
          className={cn(styles.content, { [styles["menu-open"]]: isMenuOpen })}
        >
          <div
            className={cn(styles.wrapper, {
              [styles["menu-open"]]: isMenuOpen,
            })}
          >
            {routes}
          </div>
        </main>
        <Overlay
          opened={isMenuOpen}
          shouldPreventBodyScroll
          className={styles.overlay}
        >
          <div
            onClick={handleOverlayClick}
            className={styles["overlay-inner"]}
          />
        </Overlay>
      </>
    );
  }
);
